import React from "react"
import Snackbar from "@material-ui/core/Snackbar"
import IconButton from "@material-ui/core/IconButton"
import { Clear } from "@material-ui/icons"

export interface SnackContextType {
  createSnack: (message: string) => number
}

const SnackContext = React.createContext<SnackContextType>({
  createSnack: () => 0,
})

interface SnackProps {
  id: number
  message: string
  open: boolean
  handleClose: () => void
}

const RenderSnack: React.FC<SnackProps> = ({
  id,
  message,
  open,
  handleClose,
}) => {
  const messageId = `message-${id}`
  return (
    <Snackbar
      data-qa-selector="snackbar"
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={open}
      autoHideDuration={6000}
      onClose={handleClose}
      ContentProps={{
        "aria-describedby": messageId,
      }}
      message={
        <span data-qa-selector="snackbar-message" id={messageId}>
          {message}
        </span>
      }
      action={[
        <IconButton
          data-qa-selector="snackbar-close"
          key="close"
          aria-label="Close"
          color="inherit"
          onClick={handleClose}
        >
          <Clear />
        </IconButton>,
      ]}
    />
  )
}

interface Snack {
  id: number
  message: string
  open: boolean
}

interface State {
  current: Snack | null
  queue: Snack[]
}

export const SnackProvider: React.FC = ({ children }) => {
  const [{ current, queue }, setState] = React.useState<State>({
    current: null,
    queue: [],
  })
  let uniqueId = 0

  function createSnack(message: string) {
    const id = uniqueId++
    const snack = { id, message, open: true }

    if (current) {
      setState({ current, queue: queue.concat(snack) })
    } else {
      setState({ queue, current: snack })
    }

    return id
  }

  function handleClose() {
    setState((currentState) => {
      if (currentState.current) {
        currentState.current.open = false
      }
      return currentState
    })
    // time to snack close animation
    setTimeout(openNext, 1000)
  }

  function openNext() {
    const next = queue.pop() ?? null
    setState({ current: next, queue })
  }

  return (
    <SnackContext.Provider value={{ createSnack }}>
      {current && (
        <RenderSnack key={current.id} {...current} handleClose={handleClose} />
      )}
      {children}
    </SnackContext.Provider>
  )
}

export default SnackContext
